import { Component } from '@theme/component';
import { isClickedOutside, lockScroll, unlockScroll } from '@theme/utilities';
import {
  OPEN_CEP_POPUP_EVENT,
  formatCep,
  getStoredCep,
  lookupCep,
  normalizeCepDigits,
  saveCep,
} from '@theme/cep-session';

/**
 * @typedef {object} CepPopupRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLInputElement} cepInput
 * @property {HTMLElement} errorMessage
 * @property {HTMLButtonElement} submitButton
 */

/**
 * Standalone CEP modal — does not extend DialogComponent, so opening is not
 * deferred behind nested requestAnimationFrame / requiredRefs races.
 *
 * @extends {Component<CepPopupRefs>}
 */
class CepPopupComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {boolean} */
  #forceOpen = false;

  /** @type {boolean} */
  #isSubmitting = false;

  /** @type {number} */
  #openTimer = 0;

  connectedCallback() {
    super.connectedCallback();

    const dialog = this.#dialog();
    dialog?.addEventListener('close', this.#handleDialogClose);
    dialog?.addEventListener('cancel', this.#handleDialogCancel);
    document.addEventListener(OPEN_CEP_POPUP_EVENT, this.#handleOpenRequest);
    this.addEventListener('click', this.#handleOutsideClick);

    this.#syncRequiredState();
    this.#scheduleForcedOpen();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#openTimer);
    const dialog = this.#dialog();
    dialog?.removeEventListener('close', this.#handleDialogClose);
    dialog?.removeEventListener('cancel', this.#handleDialogCancel);
    document.removeEventListener(OPEN_CEP_POPUP_EVENT, this.#handleOpenRequest);
    this.removeEventListener('click', this.#handleOutsideClick);
    unlockScroll(dialog);
    super.disconnectedCallback();
  }

  /** @returns {HTMLDialogElement | null} */
  #dialog() {
    const fromRefs = this.refs?.dialog;
    if (fromRefs instanceof HTMLDialogElement) return fromRefs;

    const fromDom = this.querySelector('dialog');
    return fromDom instanceof HTMLDialogElement ? fromDom : null;
  }

  #syncRequiredState() {
    if (getStoredCep()) {
      this.#forceOpen = false;
      this.removeAttribute('data-required');
      return;
    }

    this.#forceOpen = true;
    this.setAttribute('data-required', '');
  }

  #shouldLockClose() {
    return this.#forceOpen || this.hasAttribute('data-required');
  }

  /**
   * @returns {boolean}
   */
  #openNow() {
    this.updatedCallback();
    const dialog = this.#dialog();
    if (!(dialog instanceof HTMLDialogElement)) return false;
    if (dialog.open) return true;

    try {
      lockScroll(dialog);
      dialog.showModal();
    } catch (error) {
      console.error('[cep-popup] showModal failed', error);
      dialog.setAttribute('open', '');
      lockScroll(dialog);
    }

    return Boolean(dialog.open || dialog.hasAttribute('open'));
  }

  #scheduleForcedOpen() {
    window.clearTimeout(this.#openTimer);
    if (getStoredCep()) return;

    this.#forceOpen = true;
    this.setAttribute('data-required', '');

    let attempts = 0;
    const maxAttempts = 40;

    const tick = () => {
      if (!this.isConnected || getStoredCep()) return;
      if (this.#openNow()) return;

      attempts += 1;
      if (attempts >= maxAttempts) {
        console.error('[cep-popup] Unable to open required CEP dialog after retries');
        return;
      }

      this.#openTimer = window.setTimeout(tick, 50);
    };

    // Run ASAP, then retry while the custom element / dialog settle.
    this.#openTimer = window.setTimeout(tick, 0);
  }

  #handleDialogClose = () => {
    unlockScroll(this.#dialog());

    // Native Escape / dismiss must not leave a required popup closed.
    if (this.#shouldLockClose() && !getStoredCep()) {
      this.#scheduleForcedOpen();
    }
  };

  /**
   * @param {Event} event
   */
  #handleDialogCancel = (event) => {
    if (!this.#shouldLockClose()) return;
    event.preventDefault();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    const dialog = this.#dialog();
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
    if (this.#shouldLockClose()) return;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  #handleOpenRequest = () => {
    this.#forceOpen = false;
    this.removeAttribute('data-required');
    this.updatedCallback();

    const stored = getStoredCep();
    if (stored && this.refs.cepInput) {
      this.refs.cepInput.value = stored;
    }

    if (!this.#openNow()) {
      console.error('[cep-popup] Failed to open dialog on request');
    }
  };

  showDialog() {
    this.#openNow();
  }

  closeDialog = () => {
    if (this.#shouldLockClose()) return;

    const dialog = this.#dialog();
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) {
      unlockScroll(dialog);
      return;
    }

    dialog.classList.add('dialog-closing');

    const finish = () => {
      dialog.removeEventListener('animationend', finish);
      dialog.classList.remove('dialog-closing');
      if (dialog.open) dialog.close();
      unlockScroll(dialog);
    };

    dialog.addEventListener('animationend', finish, { once: true });
    window.setTimeout(finish, 400);
  };

  /**
   * @param {InputEvent} event
   */
  handleCepInput = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const digits = normalizeCepDigits(input.value);
    if (digits.length <= 5) {
      input.value = digits;
      return;
    }
    input.value = `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  clearError() {
    this.refs.errorMessage?.classList.add('hidden');
    if (this.refs.errorMessage) this.refs.errorMessage.textContent = '';
  }

  /**
   * @param {string} message
   */
  showError(message) {
    if (!this.refs.errorMessage) return;
    this.refs.errorMessage.textContent = message;
    this.refs.errorMessage.classList.remove('hidden');
  }

  async confirmCep() {
    if (this.#isSubmitting) return;

    this.updatedCallback();
    const input = this.refs.cepInput;
    if (!input) return;

    this.clearError();
    const digits = normalizeCepDigits(input.value);

    if (digits.length !== 8) {
      this.showError(Theme.translations?.cep_invalid || 'Informe um CEP válido.');
      return;
    }

    this.#isSubmitting = true;
    this.refs.submitButton?.setAttribute('disabled', 'true');
    this.refs.submitButton?.setAttribute('aria-busy', 'true');

    /** @type {{ cep: string, uf: string, localidade: string } | undefined} */
    let meta;

    try {
      meta = await lookupCep(digits);
    } catch (error) {
      if (error instanceof Error && error.message === 'invalid_cep') {
        this.showError(Theme.translations?.cep_invalid || 'Informe um CEP válido.');
      } else {
        this.showError(Theme.translations?.cep_not_found || 'CEP não encontrado. Tente novamente.');
      }
      this.#isSubmitting = false;
      this.refs.submitButton?.removeAttribute('disabled');
      this.refs.submitButton?.removeAttribute('aria-busy');
      return;
    }

    saveCep(meta.cep, { uf: meta.uf, localidade: meta.localidade });
    this.#forceOpen = false;
    this.removeAttribute('data-required');
    input.value = formatCep(meta.cep);

    const dialog = this.#dialog();
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      dialog.classList.remove('dialog-closing');
      dialog.close();
    }
    unlockScroll(dialog);

    this.#isSubmitting = false;
    this.refs.submitButton?.removeAttribute('disabled');
    this.refs.submitButton?.removeAttribute('aria-busy');
  }
}

if (!customElements.get('cep-popup-component')) {
  customElements.define('cep-popup-component', CepPopupComponent);
}

function bootRequiredCepPopup() {
  if (getStoredCep()) return;

  const popup = document.querySelector('cep-popup-component');
  if (!(popup instanceof CepPopupComponent)) {
    // Element exists but has not upgraded yet — wait for define.
    return;
  }

  popup.showDialog();
}

customElements.whenDefined('cep-popup-component').then(() => {
  bootRequiredCepPopup();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootRequiredCepPopup, { once: true });
  } else {
    window.setTimeout(bootRequiredCepPopup, 0);
    window.setTimeout(bootRequiredCepPopup, 250);
    window.setTimeout(bootRequiredCepPopup, 1000);
  }
});

export { OPEN_CEP_POPUP_EVENT };
